import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { formatDateTime } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import { ROLE_LABEL } from '../../lib/roleRouting';
import StatusBadge from '../../components/StatusBadge';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useFlashTimeout } from '../../hooks/useFlashTimeout';
import adminService, {
  type AdminUserSummary,
  type ListUsersParams,
  type UserRole,
} from '../../services/admin.service';
import {
  ActionDialog,
  RowMenu,
  ROLE_TONE,
  extractError,
  type ActionKind,
  type ActionState,
} from './_userActions';
import CreateStaffDialog from './CreateStaffDialog';
import PendingStaffChanges from './PendingStaffChanges';

/** Action set available for internal staff rows. `force_password` is
 *  kept (the menu auto-disables for Google SSO via auth_method check),
 *  but `change_role` is restricted server-side and UI-side to staff
 *  roles only (memory: invitations_client_only — never cross to client). */
const STAFF_ACTIONS: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'deactivate',
  'activate',
  'force_password',
  'unlock',
  'revoke_sessions',
  'change_role',
  'change_timezone',
  'reassign_clients',
  'assign_manager',
  'totp_reset',
]);

export default function AdminStaff(): JSX.Element {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  // How many match in all: the list shows the first hundred and says when there are more.
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [action, setAction] = useState<ActionState | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  // Bumped after every act, so the changes waiting for a second super admin are read again.
  const [changesKey, setChangesKey] = useState(0);

  const debouncedSearch = useDebouncedValue(search);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ListUsersParams = { limit: 100, exclude_role: 'client' };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (roleFilter) params.role = roleFilter;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      const res = await adminService.listUsers(params);
      setUsers(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const activeAgents = useMemo(
    () => users.filter((u) => u.role === 'agent' && u.is_active),
    [users],
  );
  // List of active managers used by the assign-manager dialog. The
  // current filter (paged listing) may not include all managers, so
  // we fall back on the same `users` array — admins can change the
  // role filter to "Manager" if a target manager isn't on the current
  // page.
  const activeManagers = useMemo(
    () => users.filter((u) => u.role === 'manager' && u.is_active),
    [users],
  );

  useFlashTimeout(flash, setFlash);

  const handleDone = (msg: string): void => {
    setFlash(msg);
    setAction(null);
    setChangesKey((k) => k + 1);
    reload();
  };

  return (
    <>
      <PageHeader
        title="Staff"
        subtitle="Internal staff accounts (Google SSO). Activate / deactivate, change roles, manage sessions and TOTP. A change to a manager or a super admin takes two super admins."
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: tokens.infoText, color: '#FFFFFF' }}
          >
            + Add staff
          </button>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: tokens.successBg, border: `1px solid ${tokens.successBorder}`, color: tokens.successText }}
        >
          {flash}
        </div>
      )}

      <PendingStaffChanges
        refreshKey={changesKey}
        onDecided={(msg) => {
          setFlash(msg);
          reload();
        }}
      />

      <div
        className="rounded-2xl p-5 mb-5 flex flex-wrap gap-3 items-end"
        style={{ background: tokens.surface }}
      >
        <label className="flex-1 min-w-[220px]">
          <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="email or name"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${tokens.border}` }}
          />
        </label>
        <label>
          <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>Role</span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter((e.target.value as UserRole | '') || '')}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${tokens.border}` }}
          >
            <option value="">All staff</option>
            <option value="agent">Agent</option>
            <option value="manager">Manager</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </label>
        <label>
          <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${tokens.border}` }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
        >
          {error}
        </div>
      )}

      <div className="rounded-2xl p-5 overflow-x-auto" style={{ background: tokens.surface }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: tokens.textMuted, borderBottom: `1px solid ${tokens.border}` }}>
              <th className="text-left py-2 pr-3">User</th>
              <th className="text-left py-2 pr-3">Role</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-left py-2 pr-3">Security</th>
              <th className="text-left py-2 pr-3">Last login</th>
              <th className="text-right py-2 pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && total > users.length && (
              <tr>
                <td colSpan={6} className="py-2 text-center text-xs" style={{ color: tokens.textMuted }}>
                  Showing the first {users.length} of {total} staff members. Search to find the rest.
                </td>
              </tr>
            )}
            {loading && (
              <tr><td colSpan={6} className="py-6 text-center" style={{ color: tokens.textMuted }}>Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center" style={{ color: tokens.textMuted }}>No staff match the current filters.</td></tr>
            )}
            {users.map((u) => {
              const tone = ROLE_TONE[u.role];
              const locked = u.lockout_until && new Date(u.lockout_until) > new Date();
              return (
                <tr key={u.id} style={{ borderBottom: `1px solid ${tokens.border}` }}>
                  <td className="py-3 pr-3">
                    <div className="font-medium" style={{ color: tokens.textPrimary }}>{fullName(u)}</div>
                    <div className="text-xs font-mono" style={{ color: tokens.textMuted }}>{u.email}</div>
                  </td>
                  <td className="py-3 pr-3">
                    <StatusBadge tone={tone}>{ROLE_LABEL[u.role]}</StatusBadge>
                  </td>
                  <td className="py-3 pr-3">
                    <StatusBadge tone={u.is_active ? 'success' : 'danger'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </td>
                  <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                    <div>TOTP: {u.totp_enabled ? 'on' : 'off'}</div>
                    <div>Failed: {u.failed_attempts}{locked ? ' · LOCKED' : ''}</div>
                    <div>TZ: {u.timezone ?? <span style={{ fontStyle: 'italic' }}>not set</span>}</div>
                    {u.role === 'agent' && (
                      <div>
                        Manager: {u.manager_name ?? (
                          <span style={{ fontStyle: 'italic', color: tokens.errorText }}>
                            none (fan-out fallback)
                          </span>
                        )}
                      </div>
                    )}
                    {u.must_change_password && <div style={{ color: tokens.errorText }}>Must change pwd</div>}
                  </td>
                  <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                    {formatDateTime(u.last_login_at)}
                  </td>
                  <td className="py-3 pr-3 text-right">
                    <RowMenu
                      user={u}
                      allowedActions={STAFF_ACTIONS}
                      onPick={(kind) => setAction({ kind, target: u })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {action && (
        <ActionDialog
          state={action}
          activeAgents={activeAgents}
          activeManagers={activeManagers}
          onClose={() => setAction(null)}
          onDone={handleDone}
        />
      )}

      {showCreate && (
        <CreateStaffDialog
          activeManagers={activeManagers}
          onClose={() => setShowCreate(false)}
          onDone={(msg) => {
            setShowCreate(false);
            handleDone(msg);
          }}
        />
      )}
    </>
  );
}
